import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Send, Sparkles, ArrowLeft, Bot, User, Lock, Zap, Crown, 
  MapPin, Plane, Calendar, Euro, RefreshCw, X,
  MessageCircle, ChevronRight, Globe, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { PackageCard } from '../components/PackageCard';
import { toast } from 'sonner';
import { sanitizeHTML } from '../lib/security';
import logger from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Quick suggestion chips
const QUICK_SUGGESTIONS = [
  { icon: MapPin, label: "Din București", query: "Vreau să plec din București" },
  { icon: MapPin, label: "Din Cluj", query: "Vreau să plec din Cluj-Napoca" },
  { icon: Globe, label: "Italia", query: "Vreau un circuit în Italia" },
  { icon: Globe, label: "Grecia", query: "Caut un circuit în Grecia" },
  { icon: Euro, label: "Buget 500€", query: "Bugetul meu este de 500 euro" },
  { icon: Calendar, label: "Vara", query: "Vreau să călătoresc în iulie-august" },
];

// Example prompts
const EXAMPLE_PROMPTS = [
  "Vreau un circuit în Italia cu plecare din București",
  "Ce circuite aveți cu plecare din Cluj către Grecia?",
  "Caut o vacanță pentru 2 persoane în august, buget 1000€",
];

// Extra messages packages
const EXTRA_PACKAGES = [
  { id: "1.99", price: 1.99, messages: 10 },
  { id: "3.99", price: 3.99, messages: 25 },
  { id: "6.99", price: 6.99, messages: 50 },
  { id: "11.99", price: 11.99, messages: 100 }
];

// Typing indicator
const TypingIndicator = () => (
  <div className="flex justify-start animate-fade-in">
    <div className="max-w-[85%] rounded-2xl rounded-bl-md px-4 py-3 bg-gradient-to-br from-turquoise/15 to-navy/40 border border-turquoise/20">
      <div className="flex items-center gap-3">
        <div className="w-7 h-7 rounded-full bg-turquoise/20 flex items-center justify-center">
          <Bot className="w-4 h-4 text-turquoise" />
        </div>
        <div className="flex gap-1.5">
          <span className="w-2 h-2 bg-turquoise/60 rounded-full animate-pulse" style={{ animationDelay: '0ms' }} />
          <span className="w-2 h-2 bg-turquoise/60 rounded-full animate-pulse" style={{ animationDelay: '200ms' }} />
          <span className="w-2 h-2 bg-turquoise/60 rounded-full animate-pulse" style={{ animationDelay: '400ms' }} />
        </div>
        <span className="text-turquoise/70 text-sm ml-1">Se gândește...</span>
      </div>
    </div>
  </div>
);

// Message bubble with markdown-like rendering
const MessageBubble = ({ message, index }) => {
  const isUser = message.role === 'user';
  
  const renderContent = (content) => {
    if (isUser) return <p className="whitespace-pre-wrap">{sanitizeHTML(content)}</p>;
    
    return content.split('\n').map((line, i) => {
      // Bold text - sanitize after processing
      line = line.replace(/\*\*(.+?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>');
      const sanitizedLine = sanitizeHTML(line);
      
      // Bullet points
      if (line.trim().startsWith('- ') || line.trim().startsWith('• ')) {
        const bulletContent = sanitizeHTML(line.replace(/^[\s]*[-•]\s*/, ''));
        return (
          <div key={i} className="flex items-start gap-2 my-1 ml-1">
            <ChevronRight className="w-3 h-3 text-gold mt-1.5 flex-shrink-0" />
            <span dangerouslySetInnerHTML={{ __html: bulletContent }} />
          </div>
        );
      }
      
      // Emoji indicators
      if (line.includes('📍') || line.includes('✈️') || line.includes('📅') || line.includes('💡')) {
        return <p key={i} className="my-1" dangerouslySetInnerHTML={{ __html: sanitizedLine }} />;
      }
      
      if (line.trim()) {
        return <p key={i} className="my-1" dangerouslySetInnerHTML={{ __html: sanitizedLine }} />;
      }
      return <br key={i} />;
    });
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} animate-fade-in`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-3 ${
          isUser
            ? 'bg-gradient-to-br from-gold/20 to-gold/5 border border-gold/20 rounded-br-md'
            : 'bg-gradient-to-br from-turquoise/15 to-navy/40 border border-turquoise/15 rounded-bl-md'
        }`}
        data-testid={`chat-message-${index}`}
      >
        <div className="flex items-start gap-3">
          {!isUser && (
            <div className="w-7 h-7 rounded-full bg-turquoise/20 flex items-center justify-center flex-shrink-0">
              <Bot className="w-4 h-4 text-turquoise" />
            </div>
          )}
          <div className={`text-sm leading-relaxed ${isUser ? 'text-white' : 'text-white/90'}`}>
            {renderContent(message.content)}
          </div>
          {isUser && (
            <div className="w-7 h-7 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
              <User className="w-4 h-4 text-gold" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Suggestion chip
const SuggestionChip = ({ icon: Icon, label, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 
               hover:bg-gold/10 hover:border-gold/30 hover:text-gold transition-all text-xs text-white/70
               disabled:opacity-50 disabled:cursor-not-allowed"
  >
    <Icon className="w-3 h-3" />
    {label}
  </button>
);

// Extra message card
const ExtraCard = ({ pkg, onPurchase, loading }) => (
  <div className="glass rounded-xl p-3 hover:border-gold/30 transition-all border border-white/10">
    <div className="flex items-center justify-between mb-2">
      <Zap className="w-4 h-4 text-gold" />
      <span className="text-xs text-gold/80 bg-gold/10 px-2 py-0.5 rounded-full">+{pkg.messages}</span>
    </div>
    <p className="font-bold text-lg text-gold">{pkg.price.toFixed(2)} €</p>
    <Button onClick={() => onPurchase(pkg.id)} disabled={loading} size="sm" className="w-full btn-gold mt-2 text-xs">
      Cumpără
    </Button>
  </div>
);

export default function AIChatPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [recommendedPackages, setRecommendedPackages] = useState([]);
  const [sessionId] = useState(() => {
    const stored = localStorage.getItem('vaai_chat_session');
    if (stored) return stored;
    const newId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('vaai_chat_session', newId);
    return newId;
  });
  const [usage, setUsage] = useState(null);
  const [limitReached, setLimitReached] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll only within chat container, not the page
  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    // Small delay to ensure DOM is updated
    const timer = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timer);
  }, [messages, scrollToBottom]);

  // Load usage
  const loadUsage = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/usage/${sessionId}`);
      if (response.ok) {
        const data = await response.json();
        setUsage(data);
        setLimitReached(!data.can_send);
      }
    } catch (error) {
      console.error('Failed to load usage:', error);
    }
  }, [sessionId]);

  // Initial load
  useEffect(() => {
    loadUsage();
    // Load chat history
    const loadHistory = async () => {
      try {
        const response = await fetch(`${API_URL}/api/chat/history/${sessionId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.messages && data.messages.length > 0) {
            setMessages(data.messages);
          } else {
            // Show initial greeting
            setMessages([{
              role: 'assistant',
              content: '**Bună!** Sunt VaAi, asistentul tău pentru vacanțe.\n\nSpune-mi de unde vrei să **pleci**, ce **destinație** te interesează și **bugetul** tău - găsesc cele mai bune oferte pentru tine!\n\n💡 *Exemplu: "Vreau un circuit în Italia cu plecare din București"*'
            }]);
          }
        } else {
          setMessages([{
            role: 'assistant',
            content: '**Bună!** Sunt VaAi, asistentul tău pentru vacanțe.\n\nSpune-mi de unde vrei să **pleci**, ce **destinație** te interesează și **bugetul** tău - găsesc cele mai bune oferte pentru tine!\n\n💡 *Exemplu: "Vreau un circuit în Italia cu plecare din București"*'
          }]);
        }
      } catch (error) {
        setMessages([{
          role: 'assistant',
          content: '**Bună!** Sunt VaAi, asistentul tău pentru vacanțe.\n\nSpune-mi de unde vrei să **pleci**, ce **destinație** te interesează și **bugetul** tău - găsesc cele mai bune oferte pentru tine!'
        }]);
      }
    };
    loadHistory();
  }, [sessionId, loadUsage]);

  // Payment success handler
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');
    const stripeSessionId = urlParams.get('session_id');
    
    if (payment === 'success' && stripeSessionId) {
      window.history.replaceState({}, '', window.location.pathname);
      toast.success('Plată reușită! Mesajele au fost adăugate.');
      loadUsage();
      setLimitReached(false);
    } else if (payment === 'cancelled') {
      window.history.replaceState({}, '', window.location.pathname);
      toast.error('Plata a fost anulată.');
    }
  }, [loadUsage]);

  const startNewConversation = () => {
    const newId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('vaai_chat_session', newId);
    window.location.reload();
  };

  const handlePurchase = async (packageId) => {
    setPurchaseLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/checkout/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          package_id: packageId,
          payment_type: 'extra_messages',
          success_url: `${window.location.origin}/ai-chat?payment=success&session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${window.location.origin}/ai-chat?payment=cancelled`,
          session_id: sessionId
        })
      });
      const data = await response.json();
      if (data.url) window.location.href = data.url;
    } catch (error) {
      toast.error('Eroare la procesarea plății.');
    } finally {
      setPurchaseLoading(false);
    }
  };

  const sendMessage = async (e, customMessage = null) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    const messageToSend = customMessage || input.trim();
    if (!messageToSend || loading) return;

    if (usage && !usage.can_send) {
      setLimitReached(true);
      return;
    }

    // Prevent page scroll by keeping focus management simple
    const currentScrollY = window.scrollY;
    
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: messageToSend }]);
    setLoading(true);
    
    // Restore scroll position if it changed
    requestAnimationFrame(() => {
      if (Math.abs(window.scrollY - currentScrollY) > 50) {
        window.scrollTo({ top: currentScrollY, behavior: 'instant' });
      }
    });

    try {
      const token = localStorage.getItem('token');
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 25000);
      
      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ message: messageToSend, session_id: sessionId }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      const data = await response.json();
      
      if (data.response === '__LIMIT_REACHED__') {
        await loadUsage();
        setLimitReached(true);
        setMessages(prev => prev.slice(0, -1));
        return;
      }

      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      
      if (data.recommended_packages?.length > 0) {
        setRecommendedPackages(data.recommended_packages);
      }
      
      await loadUsage();
    } catch (error) {
      console.error('Chat error:', error);
      const errorMsg = error.name === 'AbortError' 
        ? 'Răspunsul durează prea mult. Te rog încearcă din nou cu o întrebare mai specifică (ex: "circuit Italia în mai").'
        : 'Momentan serviciul AI nu este disponibil. Te rog încearcă din nou în câteva secunde.';
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: errorMsg
      }]);
    } finally {
      setLoading(false);
    }
  };

  const remainingMessages = usage ? (
    usage.is_unlimited ? '∞' : Math.max(0, usage.messages_limit + usage.extra_messages - usage.messages_used)
  ) : '...';

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy via-navy to-navy-dark">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-navy/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 text-white/70 hover:text-gold transition-colors">
                <ArrowLeft className="w-5 h-5" />
                <span className="text-sm hidden sm:inline">Înapoi</span>
              </Link>
              <div className="h-6 w-px bg-white/20" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gold to-gold/60 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-navy" />
                </div>
                <div>
                  <h1 className="text-white font-semibold text-sm">VaAi Assistant</h1>
                  <p className="text-white/50 text-xs">Online</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {usage && (
                <span className="text-xs text-gold bg-gold/10 px-3 py-1 rounded-full">
                  {remainingMessages} mesaje
                </span>
              )}
              {messages.length > 1 && (
                <button
                  onClick={startNewConversation}
                  className="flex items-center gap-1 text-xs text-white/50 hover:text-gold transition-colors px-2 py-1 rounded-lg hover:bg-white/5"
                >
                  <RefreshCw className="w-3 h-3" />
                  <span className="hidden sm:inline">Conversație nouă</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Chat Column */}
          <div className="lg:col-span-5">
            <div className="bg-gradient-to-b from-white/5 to-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 h-[calc(100vh-180px)] flex flex-col">
              {/* Messages */}
              <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4" style={{ overflowAnchor: 'none' }}>
                {messages.map((msg, i) => (
                  <MessageBubble key={i} message={msg} index={i} />
                ))}
                {loading && <TypingIndicator />}
              </div>

              {/* Quick Suggestions */}
              {messages.length > 0 && messages.length < 5 && !loading && !limitReached && (
                <div className="px-4 py-2 border-t border-white/5">
                  <div className="flex flex-wrap gap-1.5">
                    {QUICK_SUGGESTIONS.map((s, i) => (
                      <SuggestionChip
                        key={i}
                        icon={s.icon}
                        label={s.label}
                        onClick={() => sendMessage(null, s.query)}
                        disabled={loading || limitReached}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Input */}
              <div className="p-4 border-t border-white/10">
                {limitReached ? (
                  <div className="text-center py-3">
                    <div className="flex items-center justify-center gap-2 text-gold mb-3">
                      <Lock className="w-5 h-5" />
                      <span className="font-medium">Limită atinsă</span>
                    </div>
                    <p className="text-white/50 text-xs mb-4">
                      Ai folosit {usage?.messages_used}/{usage?.messages_limit} mesaje
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {EXTRA_PACKAGES.map(pkg => (
                        <ExtraCard key={pkg.id} pkg={pkg} onPurchase={handlePurchase} loading={purchaseLoading} />
                      ))}
                    </div>
                  </div>
                ) : (
                  <form onSubmit={sendMessage} className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        ref={inputRef}
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Scrie mesajul tău..."
                        className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 pr-10 text-white placeholder:text-white/30 focus:border-gold focus:ring-1 focus:ring-gold/50 transition-all text-sm"
                        disabled={loading || limitReached}
                        data-testid="chat-input"
                      />
                      {input && (
                        <button type="button" onClick={() => setInput('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <Button
                      type="submit"
                      disabled={loading || !input.trim() || limitReached}
                      className="btn-gold px-4 rounded-xl"
                      data-testid="chat-send-btn"
                    >
                      <Send className="w-5 h-5" />
                    </Button>
                  </form>
                )}
                
                {/* Disclaimer */}
                <div className="mt-3 px-1">
                  <p className="text-white/40 text-[10px] leading-relaxed">
                    <span className="font-medium text-white/50">Notă:</span> Recomandările generate de AI sunt orientative și pot conține erori. Toate informațiile trebuie verificate pe site-ul oficial al tour operatorului înainte de orice rezervare. VaAi nu își asumă responsabilitatea pentru eventualele diferențe.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Recommendations Column */}
          <div className="lg:col-span-7">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading font-semibold text-xl text-white flex items-center gap-2">
                  {recommendedPackages.length > 0 ? (
                    <>
                      <Sparkles className="w-5 h-5 text-gold" />
                      Recomandări pentru tine
                    </>
                  ) : (
                    <>
                      <Globe className="w-5 h-5 text-turquoise" />
                      Încearcă să întrebi
                    </>
                  )}
                </h2>
                <p className="text-white/50 text-sm mt-0.5">
                  {recommendedPackages.length > 0 
                    ? `${recommendedPackages.length} pachete găsite`
                    : 'Menționează orașul de plecare pentru rezultate precise'}
                </p>
              </div>
              {recommendedPackages.length > 0 && (
                <span className="text-xs text-white/40 bg-white/5 px-2 py-1 rounded-full flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  LIVE
                </span>
              )}
            </div>

            {recommendedPackages.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {recommendedPackages.slice(0, 6).map((pkg, i) => (
                  <div key={pkg.id} className="animate-fade-in" style={{ animationDelay: `${i * 80}ms` }}>
                    <PackageCard package={pkg} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6">
                <p className="text-white/70 text-center mb-4">Exemple de întrebări:</p>
                <div className="space-y-2">
                  {EXAMPLE_PROMPTS.map((prompt, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(null, prompt)}
                      disabled={loading || limitReached}
                      className="w-full text-left px-4 py-3 rounded-xl bg-white/5 border border-white/10 hover:bg-gold/10 hover:border-gold/30 transition-all text-white/70 text-sm group"
                    >
                      <span className="group-hover:text-gold transition-colors">"{prompt}"</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
